import React, { useState } from "react";
import { Download, Trash2, Loader2, Inbox } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const stringValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export function AdminDataTable({ section, rows, columns, loading, onDelete }) {
  const [deletingId, setDeletingId] = useState(null);

  const exportCsv = () => {
    const escape = (value) => `"${stringValue(value).replaceAll('"', '""')}"`;
    const content = [columns.map((column) => escape(column.label)).join(","), ...rows.map((row) => columns.map((column) => escape(row[column.key])).join(","))].join("\n");
    const file = new Blob([content], { type: "text/csv;charset=utf-8" });
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(file);
    anchor.download = `fyllr-${section}-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  const handleDelete = async (row) => {
    const id = row.id;
    if (!id) return;
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section data-testid={`admin-${section}-panel`}>
      <div className="flex items-center justify-between gap-4 mb-4">
        <p className="text-sm text-ink/55" data-testid={`admin-${section}-count`}>{loading ? "Loading…" : `${rows.length} record${rows.length === 1 ? "" : "s"}`}</p>
        <button type="button" onClick={exportCsv} disabled={loading || rows.length === 0} className="inline-flex h-10 items-center gap-2 border border-[#3D0A1E]/30 px-4 text-sm font-semibold text-[#3D0A1E] hover:bg-[#3D0A1E]/5 disabled:opacity-40 transition-colors" data-testid={`admin-export-${section}`}>
          <Download size={13} strokeWidth={1.5} /> Export to CSV
        </button>
      </div>
      <div className="border border-[#3D0A1E]/15 bg-[#F5EFE6] overflow-x-auto" data-testid={`admin-${section}-table-wrap`}>
        <Table data-testid={`admin-${section}-table`}>
          <TableHeader>
            <TableRow>
              {columns.map((column) => <TableHead key={column.key} className="whitespace-nowrap bg-[#3D0A1E]/5 px-6 py-4 text-sm font-semibold uppercase tracking-[0.08em] text-[#3D0A1E]" data-testid={`admin-${section}-header-${column.key}`}>{column.label}</TableHead>)}
              {onDelete && <TableHead className="px-4 py-3 text-[10px] tracking-[0.15em] uppercase text-mocha text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={columns.length + (onDelete ? 1 : 0)} className="px-4 py-12 text-center" data-testid={`admin-${section}-empty`}><Inbox className="mx-auto mb-3 text-[#3D0A1E]/40" size={28} /><p className="text-base font-semibold text-[#3D0A1E]">Nothing here yet.</p><p className="mt-1 text-sm text-[#3D0A1E]/60">This section is ready for its first record.</p></TableCell></TableRow>}
            {rows.map((row, index) => (
              <TableRow key={row.id || row.stripe_session_id || index} className="border-b border-[#3D0A1E]/10 even:bg-[#3D0A1E]/[0.025] hover:bg-[#3D0A1E]/[0.05]" data-testid={`admin-${section}-row-${row.id || index}`}>
                {columns.map((column) => <TableCell key={column.key} className="max-w-[220px] truncate px-6 py-4 text-sm text-[#3D0A1E]/80" data-testid={`admin-${section}-cell-${row.id || index}-${column.key}`}>{column.format ? column.format(row[column.key], row) : stringValue(row[column.key])}</TableCell>)}
                {onDelete && (
                  <TableCell className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleDelete(row)}
                      disabled={!row.id || deletingId === row.id}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sand px-3 py-1.5 text-xs text-ink/70 hover:border-burgundy hover:text-burgundy disabled:opacity-40 transition-colors"
                      data-testid={`admin-delete-${section}-${row.id || index}`}
                      aria-label="Delete record"
                    >
                      {deletingId === row.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} strokeWidth={1.5} />}
                    </button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
